import { useState, useCallback } from 'react';
import { MessageType } from '../../types/message';
import { ChatType } from '../../api/chats';

// Maximum number of regenerations allowed per message
export const MAX_REGENERATIONS = 30 - 1;

// Interface for regeneration history tracking
export interface RegenerationHistoryItem {
  messageId: string;
  content: string;
  regenerationNumber: number;
}

interface UseRegenerateTrackingProps {
  chatObj: ChatType | null;
  setChatObj: (chat: ChatType) => void;
  messages: MessageType[];
  handleRegenerate: (messageId: string) => Promise<void>;
}

export const useRegenerateTracking = ({
  chatObj,
  setChatObj,
  messages,
  handleRegenerate
}: UseRegenerateTrackingProps) => {
  // State for tracking regeneration history
  const [regenerationHistory, setRegenerationHistory] = useState<RegenerationHistoryItem[]>([]);
  // Track which regeneration index is selected for each message
  const [selectedRegenerationIndices, setSelectedRegenerationIndices] = useState<Record<string, number>>({});
  // Track which messages are in toggled state (user has selected a non-latest regeneration)
  const [regenerationToggledState, setRegenerationToggledState] = useState<Record<string, boolean>>({});
  // Track which messages are currently being edited
  const [editingMessageIds, setEditingMessageIds] = useState<Record<string, boolean>>({});

  // Function to mark message as being edited
  const markMessageEditing = useCallback((messageId: string, isEditing: boolean) => {
    setEditingMessageIds(prev => ({
      ...prev,
      [messageId]: isEditing
    }));
  }, []);

  // Wrap handleRegenerate to track regeneration history
  const handleRegenerateWithTracking = useCallback(async (messageId: string) => {
    try {
      // Check if message is being edited - if so, don't track regeneration
      if (editingMessageIds[messageId]) {
        return await handleRegenerate(messageId);
      }
      
      // Get the current message content before regeneration
      const messageToRegenerate = messages.find(msg => msg.id === messageId);
      if (!messageToRegenerate) return;
      
      // Check if we already have reached the max regenerations limit for this message
      const messageRegens = regenerationHistory.filter(item => item.messageId === messageId);
      const regenCount = messageRegens.length;
      
      // Don't allow more than MAX_REGENERATIONS total
      if (regenCount >= MAX_REGENERATIONS) {
        console.log(`Maximum regeneration limit (${MAX_REGENERATIONS}) reached for message ${messageId}`);
        return;
      }
      
      // Call the original regenerate function
      await handleRegenerate(messageId);
      
      // After regeneration completes, update history
      setRegenerationHistory(prev => {
        // Collect all existing regenerations for this message
        const messageRegens = prev.filter(item => item.messageId === messageId);
        
        // If this is the first regeneration, add the original message as #1
        if (messageRegens.length === 0) {
          return [
            ...prev,
            {
              messageId,
              content: messageToRegenerate.content || '',
              regenerationNumber: 1
            }
          ];
        } else {
          // Otherwise, we're adding a new regeneration
          // Get the highest regeneration number for this message
          const highestRegenNumber = Math.max(...messageRegens.map(item => item.regenerationNumber));
          
          // Create a new item with the next regeneration number
          return [
            ...prev,
            {
              messageId,
              content: messageToRegenerate.content || '',
              regenerationNumber: highestRegenNumber + 1
            }
          ];
        }
      });
      
      // Reset to the latest regeneration
      setSelectedRegenerationIndices(prev => ({
        ...prev,
        [messageId]: 0
      }));
      
      // Clear toggled state since we're now showing the latest version
      setRegenerationToggledState(prev => ({
        ...prev,
        [messageId]: false
      }));
    } catch (error) {
      console.error('Error in handleRegenerateWithTracking:', error);
    }
  }, [handleRegenerate, messages, editingMessageIds, regenerationHistory]);

  // Handle selecting a specific regeneration
  const handleSelectRegeneration = useCallback(async (messageId: string, index: number) => {
    // If message is being edited, don't allow regeneration selection
    if (editingMessageIds[messageId]) return;
    
    setSelectedRegenerationIndices(prev => ({
      ...prev,
      [messageId]: index
    }));
    
    // Check if this is the latest regeneration
    const messageRegens = regenerationHistory.filter(item => item.messageId === messageId);
    const isLatestRegeneration = index === 0;
    
    // Update toggled state based on selection
    setRegenerationToggledState(prev => ({
      ...prev,
      [messageId]: !isLatestRegeneration && messageRegens.length > 0
    }));
    
    // If toggling to a non-latest version and it's not already selected,
    // use selectRegenerationOption API to lock the message history
    if (!isLatestRegeneration && !regenerationToggledState[messageId] && chatObj) {
      try {
        // Calculate which regeneration number to select
        // We need to convert from client index to backend number
        // Client: 0 = latest, higher = older
        // Backend: 1 = oldest, higher = newer
        const regenCount = messageRegens.length;
        // Convert index to backend regeneration number (reverse the index)
        const regenerationNumber = regenCount + 1 - index;
        
        // Call the API to lock in this regeneration
        const updatedChat = await chatObj.selectRegenerationOption(messageId, regenerationNumber);
        setChatObj(updatedChat);
        
        // No need to update messages here as we're already showing the historical content
      } catch (error) {
        console.error('Error selecting regeneration option:', error);
      }
    }
  }, [chatObj, regenerationHistory, regenerationToggledState, editingMessageIds]);

  // Handle editing a message that has regeneration history
  const handleEditRegeneratedMessage = useCallback((messageId: string) => {
    // Mark this message as being edited
    markMessageEditing(messageId, true);
    
    // Always reset to the latest regeneration when editing
    setSelectedRegenerationIndices(prev => ({
      ...prev,
      [messageId]: 0
    }));
    
    // Also reset the toggled state
    setRegenerationToggledState(prev => ({
      ...prev,
      [messageId]: false
    }));
  }, [markMessageEditing]);

  // Handle completing an edit
  const completeEdit = useCallback((messageId: string) => {
    markMessageEditing(messageId, false);
    
    // After editing, we might want to clean up regeneration history
    // for this message since it's been manually edited
    setRegenerationHistory(prev => 
      prev.filter(item => item.messageId !== messageId)
    );
  }, [markMessageEditing]);

  return {
    regenerationHistory,
    selectedRegenerationIndices,
    handleRegenerateWithTracking,
    handleSelectRegeneration,
    handleEditRegeneratedMessage,
    completeEdit
  };
};

export default useRegenerateTracking; 